/**
 * Landing Page v2 - "The tool IS the page"
 * The hero contains a real, working prompt enhancer wired to the
 * anonymous try-before-signup API. Everything else supports that moment.
 */

import '../components/accordion.js'; // side effect: auto-initializes [data-accordion] on DOMContentLoaded
import { BACKEND_URL } from '../utils/config.js';
import { initHeroShader } from './hero-shader.js';
import { captureFirstTouch, getAttribution } from '../utils/attribution.js';
import { getAnonTrialState, getAnonRemaining, recordAnonEnhancement, exhaustAnonTrial, formatTrialReset } from '../utils/anon-trial.js';
import { renderPrompt } from '../utils/render-prompt.js';
import { getPricing, getTier, formatPrice, annualSavingPercent } from '../utils/pricing.js';

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ============================================
// ANONYMOUS TRIAL STATE
// utils/anon-trial.js owns the counters and windows, so the landing page and /enhance
// spend one budget and neither one hardcodes the number of free tries.
// ============================================

function getAnonBrowserToken() {
    try {
        let t = localStorage.getItem('anon_browser_token');
        if (!t) {
            t = (crypto.randomUUID && crypto.randomUUID()) ||
                `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            localStorage.setItem('anon_browser_token', t);
        }
        return t;
    } catch (e) {
        return null;
    }
}

// ============================================
// HERO ENHANCER
// ============================================

const els = {};

function cacheEls() {
    els.input = document.getElementById('heroPromptInput');
    els.charCount = document.getElementById('heroCharCount');
    els.usesLeft = document.getElementById('heroUsesLeft');
    els.btn = document.getElementById('heroEnhanceBtn');
    els.btnLabel = document.getElementById('heroEnhanceLabel');
    els.status = document.getElementById('heroStatus');
    els.result = document.getElementById('heroResult');
    els.resultText = document.getElementById('heroResultText');
    els.scoreWrap = document.getElementById('heroScoreWrap');
    els.scoreRing = document.getElementById('heroScoreRing');
    els.scoreNum = document.getElementById('heroScoreNum');
    els.scoreDelta = document.getElementById('heroScoreDelta');
    els.copyBtn = document.getElementById('heroCopyBtn');
    els.limit = document.getElementById('heroLimit');
    els.editorBar = document.querySelector('.editor-bar');
    els.analysisTeaser = document.getElementById('heroAnalysisTeaser');
    els.teaserGrid = document.getElementById('heroTeaserGrid');
    els.teaserLockText = document.getElementById('heroTeaserLockText');
    els.example = document.getElementById('heroExample');
    els.pending = document.getElementById('heroPending');
}

/**
 * Ready panel on pages that have one: the labeled example shows only until a real
 * result, a pending state, or the limit panel takes its place.
 */
let hasResult = false;
function syncReadyPanel() {
    if (els.pending) els.pending.hidden = !isEnhancing;
    if (els.example) {
        const limitShown = els.limit && !els.limit.hidden;
        const wasHidden = els.example.hidden;
        els.example.hidden = isEnhancing || hasResult || limitShown;
        if (wasHidden && !els.example.hidden) els.example.querySelectorAll('[data-clamp]').forEach((el) => refreshClamp(el, rawWords(el)));
    }
}

// ============================================
// LONG PROMPTS: fixed-height pane with a fade and a toggle that shows only on overflow.
// Nothing is display:none, so the full text stays available to assistive tech and to Copy.
// ============================================

let clampSeq = 0;

function clampFoot(container) {
    const next = container.nextElementSibling;
    return next && next.hasAttribute('data-clamp-foot') ? next : null;
}

function wordCount(text) {
    return (text.trim().match(/\S+/g) || []).length;
}

/** @param {number} [words] word count of the raw prompt; defaults to the rendered text */
function rawWords(el) {
    if (el.dataset.rawWords) return Number(el.dataset.rawWords);
    return el.id === 'heroResultText' && lastEnhanced ? wordCount(lastEnhanced) : undefined;
}

function refreshClamp(container, words) {
    const foot = clampFoot(container);
    if (!foot) return;
    const toggle = foot.querySelector('[data-clamp-toggle]');
    const wordsEl = foot.querySelector('[data-clamp-words]');
    if (!container.id) container.id = `clamp-${++clampSeq}`;

    container.classList.add('is-clamped');
    const overflows = container.scrollHeight > container.clientHeight + 8;
    container.classList.toggle('is-clamped', overflows);

    if (wordsEl) wordsEl.textContent = `${words ?? wordCount(container.textContent)} words`;
    if (foot.dataset.clampFoot === 'toggle-only') foot.hidden = !overflows;
    if (toggle) {
        toggle.hidden = !overflows;
        toggle.setAttribute('aria-controls', container.id);
        toggle.setAttribute('aria-expanded', 'false');
        toggle.querySelector('span').textContent = 'Show the full prompt';
    }
}

/** Render markup-held prompt text through the shared renderer, then size any clamp inside `root`. */
function hydratePrompts(root = document) {
    root.querySelectorAll('[data-render-prompt]').forEach((el) => {
        const text = el.textContent.trim();
        renderPrompt(el, text, { typedPrompt: el.dataset.typed || '' });
        el.removeAttribute('data-render-prompt');
        el.dataset.rawWords = String(wordCount(text));
    });
    root.querySelectorAll('[data-clamp]').forEach((el) => refreshClamp(el, rawWords(el)));
}

function initClamps() {
    document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-clamp-toggle]');
        const container = toggle?.closest('[data-clamp-foot]')?.previousElementSibling;
        if (!container) return;
        const clamped = container.classList.toggle('is-clamped');
        toggle.setAttribute('aria-expanded', String(!clamped));
        toggle.querySelector('span').textContent = clamped ? 'Show the full prompt' : 'Show less';
        if (clamped) container.scrollIntoView({ block: 'nearest' });
    });

    // Fonts and widths change line breaks: re-measure panes that are collapsed or not clampable yet
    const remeasure = () => document.querySelectorAll('[data-clamp]').forEach((el) => {
        if (!el.classList.contains('is-clamped') && clampFoot(el)?.querySelector('[data-clamp-toggle][aria-expanded="true"]')) return;
        if (el.closest('[hidden]')) return;
        refreshClamp(el, rawWords(el));
    });
    let timer = null;
    window.addEventListener('resize', () => { clearTimeout(timer); timer = setTimeout(remeasure, 150); });
    if (document.fonts?.ready) document.fonts.ready.then(remeasure);

    hydratePrompts();
}

// The 5 quality dimensions the scorer returns, in display order.
const TEASER_DIMS = [
    ['clarityStructure', 'Clarity & Structure'],
    ['contextualEnrichment', 'Context'],
    ['precisionEngineering', 'Precision'],
    ['collaborationOptimization', 'Collaboration'],
    ['outputFormatting', 'Output format']
];

/**
 * Populate the inline analysis teaser from the real anon analysis payload.
 * Renders the actual per-dimension breakdown (blurred via CSS) with a signup
 * CTA to unlock: no navigation, no wasted enhancement.
 */
function buildAnalysisTeaser(analysis) {
    if (!els.analysisTeaser) return;
    // Pages without the score grid (the SEO page shows no scores) get a plain signup prompt.
    if (!els.teaserGrid) { els.analysisTeaser.hidden = false; return; }
    const scores = analysis && analysis.scores;
    if (!scores || !scores.enhancedScores) { els.analysisTeaser.hidden = true; return; }

    const enh = scores.enhancedScores;
    const imp = scores.scoreImprovements || {};
    const rows = TEASER_DIMS
        .filter(([k]) => (imp[k] || 0) > 0)
        .map(([k, label]) => {
            const e = Math.max(0, Math.min(100, Math.round(enh[k] || 0)));
            const d = Math.round(imp[k] || 0);
            return `<div class="teaser-row"><span class="teaser-dim">${label}</span>`
                + `<span class="teaser-bar-wrap"><span class="teaser-bar" style="width:${e}%"></span></span>`
                + `<span class="teaser-imp">+${d}</span></div>`;
        });

    if (!rows.length) { els.analysisTeaser.hidden = true; return; }
    els.teaserGrid.innerHTML = rows.join('');

    const tips = (scores.insights && scores.insights.length)
        || (analysis.improvements && analysis.improvements.length) || 0;
    if (els.teaserLockText) {
        els.teaserLockText.textContent = tips > 0
            ? `Unlock all ${rows.length} dimensions scored, plus ${tips} specific ${tips === 1 ? 'fix' : 'fixes'} to push it higher.`
            : `Unlock every dimension scored, plus the specific fixes to push it higher.`;
    }

    els.analysisTeaser.hidden = false;
    if (window.lucide) window.lucide.createIcons({ nameAttr: 'data-lucide' });
}

let isEnhancing = false;
let lastEnhanced = '';
let copyResetTimer = null;
let idleBtnLabel = 'Enhance it';

function updateUsesLeft() {
    if (!els.usesLeft) return;
    const { remaining, per30Days } = getAnonTrialState();
    els.usesLeft.textContent = remaining >= per30Days
        ? `${per30Days} free tries, no account`
        : `${remaining} of ${per30Days} free tries left`;
}

function updateCharCount() {
    if (!els.charCount || !els.input) return;
    els.charCount.textContent = `${els.input.value.length} / 4000`;
}

function setStatus(message, isError = false) {
    if (!els.status) return;
    if (!message) {
        els.status.hidden = true;
        els.status.textContent = '';
        els.status.classList.remove('is-error');
        return;
    }
    els.status.hidden = false;
    els.status.textContent = message;
    els.status.classList.toggle('is-error', isError);
}

function setLoading(loading) {
    isEnhancing = loading;
    if (els.btn) els.btn.disabled = loading || !els.input.value.trim();
    if (els.btnLabel) els.btnLabel.textContent = loading ? 'Enhancing' : idleBtnLabel;
    syncReadyPanel();
}

function showLimitState() {
    if (els.limit) {
        // Returning visitor: say when the trial comes back rather than reading as a permanent wall.
        const trial = getAnonTrialState();
        const text = els.limit.querySelector('.limit-text');
        if (text) {
            text.textContent = `All ${trial.per30Days} free tries are used. The trial ${formatTrialReset(trial.resetsAt)}.`;
        }
        els.limit.hidden = false;
    }
    lockInput();
    if (els.editorBar) els.editorBar.style.display = 'none';
    setStatus('');
    syncReadyPanel();
}

/**
 * Reveal the enhanced text. Typewriter when motion is allowed so the
 * visitor watches the rewrite being performed; instant otherwise.
 */
function revealResult(text) {
    if (!els.result || !els.resultText) return;
    els.result.hidden = false;
    hasResult = true;
    syncReadyPanel();
    lastEnhanced = text;

    if (prefersReducedMotion) {
        renderPrompt(els.resultText, text);
        refreshClamp(els.resultText, wordCount(text));
        return Promise.resolve();
    }

    els.resultText.replaceChildren();
    return new Promise(resolve => {
        const total = text.length;
        const durationMs = Math.min(1600, Math.max(700, total * 4));
        const start = performance.now();
        function tick(now) {
            const progress = Math.min(1, (now - start) / durationMs);
            if (progress < 1) {
                renderPrompt(els.resultText, text.slice(0, Math.floor(progress * total)), { partial: true });
                requestAnimationFrame(tick);
            } else {
                renderPrompt(els.resultText, text);
                refreshClamp(els.resultText, wordCount(text));
                resolve();
            }
        }
        requestAnimationFrame(tick);
    });
}

/** Animate the coral conic score ring up to `score` (0-100). */
function revealScore(score, delta) {
    if (!els.scoreWrap || !els.scoreRing || !els.scoreNum) return;
    els.scoreWrap.hidden = false;

    const paint = pct => {
        els.scoreRing.style.background =
            `radial-gradient(closest-side, rgba(6, 26, 30, 0.95) 78%, transparent 80% 100%),` +
            `conic-gradient(#DB6B47 0% ${pct}%, rgba(255,255,255,0.12) ${pct}% 100%)`;
    };

    if (els.scoreDelta) {
        els.scoreDelta.textContent = delta > 0 ? `+${delta} vs. your original` : '';
    }

    if (prefersReducedMotion) {
        paint(score);
        els.scoreNum.textContent = String(score);
        return;
    }

    const start = performance.now();
    const durationMs = 800;
    function tick(now) {
        const progress = Math.min(1, (now - start) / durationMs);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(eased * score);
        paint(current);
        els.scoreNum.textContent = String(current);
        if (progress < 1) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
}

async function handleEnhance() {
    if (isEnhancing || !els.input) return;
    const prompt = els.input.value.trim();
    if (!prompt) return;

    if (getAnonRemaining() <= 0) {
        showLimitState();
        return;
    }

    capture('landing_hero_enhance_submitted', { prompt_length: prompt.length });

    setLoading(true);
    els.result.hidden = true;
    els.scoreWrap.hidden = true;
    if (els.analysisTeaser) els.analysisTeaser.hidden = true;
    // The ready panel shows its own pending line where it exists; one message only
    setStatus(els.pending ? '' : 'Rewriting your prompt');

    try {
        const anonToken = getAnonBrowserToken();
        const response = await fetch(`${BACKEND_URL}/api/enhance/anonymous`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(anonToken ? { 'X-Anon-Token': anonToken } : {})
            },
            body: JSON.stringify({ prompt, protocols: ['context'] })
        });

        if (response.status === 429) {
            // Server-side limit: mirror it locally and convert.
            exhaustAnonTrial();
            showLimitState();
            capture('landing_hero_limit_reached', { source: 'server' });
            return;
        }

        if (!response.ok) {
            let message = 'Something went wrong. Give it another try.';
            try {
                const err = await response.json();
                if (err && (err.message || err.error)) message = err.message || err.error;
            } catch (e) { /* keep default */ }
            throw new Error(message);
        }

        const result = await response.json();
        const enhanced = (result && result.enhanced) || '';
        if (!enhanced) throw new Error('The enhancer returned an empty result. Try again.');

        const remainingAfter = recordAnonEnhancement();
        updateUsesLeft();
        setStatus('');

        await revealResult(enhanced);

        const scores = result.analysis && result.analysis.scores;
        if (scores && scores.enhancedScores && typeof scores.enhancedScores.overall === 'number') {
            const overall = Math.round(scores.enhancedScores.overall);
            const original = scores.originalScores && typeof scores.originalScores.overall === 'number'
                ? Math.round(scores.originalScores.overall) : null;
            const delta = original !== null ? overall - original : 0;
            revealScore(overall, delta);
        }

        // Render the real breakdown, blurred, with an unlock CTA.
        buildAnalysisTeaser(result.analysis);

        capture('landing_hero_enhance_succeeded', {
            prompt_length: prompt.length,
            enhanced_length: enhanced.length,
            uses_remaining: remainingAfter
        });

        if (remainingAfter <= 0) {
            // Deliver the value first, then show the convert panel beneath it.
            showLimitStateKeepResult();
            capture('landing_hero_limit_reached', { source: 'client' });
        }
    } catch (error) {
        console.error('Hero enhancement failed:', error);
        if (lastEnhanced && els.result) els.result.hidden = false;
        const offline = error instanceof TypeError; // network failure
        setStatus(
            offline
                ? 'Could not reach the enhancer right now. Try again in a minute.'
                : error.message,
            true
        );
        capture('landing_hero_enhance_failed', { offline });
    } finally {
        setLoading(false);
    }
}

/** Limit panel below a delivered result: input disabled, result stays visible. */
function showLimitStateKeepResult() {
    if (els.limit) {
        const trial = getAnonTrialState();
        const text = els.limit.querySelector('.limit-text');
        if (text) {
            text.textContent = `That was your last free try. The trial ${formatTrialReset(trial.resetsAt)}.`;
        }
        els.limit.hidden = false;
    }
    // One signup prompt at a time: the plain teaser repeats the limit panel's ask.
    if (els.analysisTeaser && !els.teaserGrid) els.analysisTeaser.hidden = true;
    lockInput();
    if (els.editorBar) els.editorBar.style.display = 'none';
}

/** The grader keeps its input usable after the trial ends: grading is unlimited, only fixing is not. */
function lockInput() {
    if (els.input && !els.input.hasAttribute('data-keep-enabled')) els.input.disabled = true;
}

async function handleCopy() {
    if (!lastEnhanced || !els.copyBtn) return;
    let ok = false;
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(lastEnhanced);
            ok = true;
        }
    } catch (e) { /* fall through */ }
    if (!ok) {
        try {
            const ta = document.createElement('textarea');
            ta.value = lastEnhanced;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.top = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            ok = document.execCommand('copy');
            document.body.removeChild(ta);
        } catch (e) { ok = false; }
    }
    if (!ok) return;

    capture('landing_hero_copy_clicked', {});
    if (copyResetTimer) clearTimeout(copyResetTimer);
    els.copyBtn.classList.add('copied');
    els.copyBtn.innerHTML = '<i data-lucide="check"></i><span>Copied</span>';
    if (window.lucide) window.lucide.createIcons({ nameAttr: 'data-lucide' });
    copyResetTimer = setTimeout(() => {
        els.copyBtn.classList.remove('copied');
        els.copyBtn.innerHTML = '<i data-lucide="copy"></i><span>Copy</span>';
        if (window.lucide) window.lucide.createIcons({ nameAttr: 'data-lucide' });
        copyResetTimer = null;
    }, 2000);
}

// ============================================
// ANALYTICS
// ============================================

function capture(event, props) {
    if (typeof window.posthog !== 'undefined') {
        // Attribution first so an explicit prop of the same name still wins.
        window.posthog.capture(event, { ...getAttribution(), ...props, page: 'landing' });
    }
}

// ============================================
// PAGE WIRING
// ============================================

function initHeroEnhancer() {
    cacheEls();
    if (!els.input || !els.btn) return;

    // Respect whatever idle label the page shipped with (e.g. "Generate
    // prompt" on the SEO variant) instead of hardcoding "Enhance it".
    if (els.btnLabel && els.btnLabel.textContent.trim()) {
        idleBtnLabel = els.btnLabel.textContent.trim();
    }

    updateUsesLeft();
    updateCharCount();
    els.btn.disabled = true;

    els.input.addEventListener('input', () => {
        updateCharCount();
        if (!isEnhancing) els.btn.disabled = !els.input.value.trim();
    });

    // Cmd/Ctrl+Enter submits, like every editor
    els.input.addEventListener('keydown', e => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleEnhance();
        }
    });

    els.btn.addEventListener('click', handleEnhance);
    if (els.copyBtn) els.copyBtn.addEventListener('click', handleCopy);

    const teaserCta = els.analysisTeaser && els.analysisTeaser.querySelector('.teaser-cta');
    if (teaserCta) teaserCta.addEventListener('click', () => capture('landing_teaser_unlock_clicked', {}));

    // Out of free uses before they even type: show the convert panel.
    if (getAnonRemaining() <= 0) {
        showLimitState();
    }
}

function initBackToEditor() {
    const btn = document.getElementById('backToEditorBtn');
    if (!btn) return;
    btn.addEventListener('click', () => {
        (document.getElementById('heroEditor') || document.getElementById('main-content'))?.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth'
        });
        setTimeout(() => els.input?.focus(), prefersReducedMotion ? 0 : 450);
        capture('landing_lastcall_clicked', {});
    });
}

function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            const href = this.getAttribute('href');
            if (href === '#') return;
            const target = document.querySelector(href);
            if (target) {
                e.preventDefault();
                target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
            }
        });
    });
}

/**
 * Receipts gauge: clicking a lane swaps the rough/ready split below for that
 * receipt's <template>. Content lives only in markup (index.html), never
 * duplicated into JS strings — this just moves it and re-renders icons.
 */
function initReceiptsGauge() {
    const lanes = document.querySelectorAll('.receipt-lane');
    const split = document.getElementById('receiptSplit');
    if (!lanes.length || !split) return;

    const section = split.closest('section');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const show = (lane, fade) => {
        const template = document.getElementById(`receipt-panel-${lane.dataset.receipt}`);
        if (!template) return;
        lanes.forEach(l => l.setAttribute('aria-pressed', String(l === lane)));
        const swap = () => {
            split.replaceChildren(template.content.cloneNode(true));
            if (typeof lucide !== 'undefined') lucide.createIcons();
            hydratePrompts(split);
        };
        if (!fade || reduceMotion) return swap();
        const fadeOut = split.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 220, easing: 'ease-in', fill: 'forwards' });
        fadeOut.finished.then(() => {
            swap();
            split.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 320, easing: 'ease-out' });
            fadeOut.cancel();
        });
    };

    lanes.forEach(lane => {
        lane.addEventListener('click', event => {
            // A real click hands control to the visitor; the loop stops for good.
            if (lane.getAttribute('aria-pressed') === 'true') return;
            show(lane, true);
        });
    });

    if (reduceMotion || !section) return;

    // Preload the lettering inside templates, which doesn't fetch until cloned.
    const preload = () => document.querySelectorAll('template[id^="receipt-panel-"]').forEach(t =>
        t.content.querySelectorAll('img').forEach(img => { new Image().src = img.src; }));
    (window.requestIdleCallback || setTimeout)(preload);

    const INTERVAL = 7000;
    const bars = Array.from(lanes, lane => {
        const bar = document.createElement('span');
        bar.className = 'receipt-progress';
        bar.setAttribute('aria-hidden', 'true');
        lane.appendChild(bar);
        return bar;
    });
    let index = Array.from(lanes).findIndex(l => l.getAttribute('aria-pressed') === 'true');
    let timer = null;
    let stopped = false;
    const pausedBy = new Set(['offscreen']);
    if (document.hidden) pausedBy.add('hidden');

    const run = () => {
        timer?.cancel();
        timer = bars[index].animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
            { duration: INTERVAL, easing: 'linear' });
        if (pausedBy.size) timer.pause();
        const current = timer;
        current.finished.then(() => {
            if (stopped || current !== timer) return;
            index = (index + 1) % lanes.length;
            show(lanes[index], true);
            run();
        }).catch(() => {});
    };

    const setPaused = (reason, paused) => {
        paused ? pausedBy.add(reason) : pausedBy.delete(reason);
        if (!timer || stopped) return;
        pausedBy.size ? timer.pause() : timer.play();
    };

    // A real click hands control to the visitor; the loop stops for good. Expanding a prompt counts too.
    const stop = event => {
        if (!event.isTrusted || stopped) return;
        stopped = true;
        timer?.cancel();
        bars.forEach(bar => bar.remove());
    };
    lanes.forEach(lane => lane.addEventListener('click', stop));
    split.addEventListener('click', event => {
        if (event.target.closest('[data-clamp-toggle]')) stop(event);
    });

    // Pause only for keyboard focus, not mouse hover: a resting pointer would freeze the loop.
    section.addEventListener('focusin', event => {
        if (event.target.matches(':focus-visible')) setPaused('focus', true);
    });
    section.addEventListener('focusout', event => {
        if (!section.contains(event.relatedTarget)) setPaused('focus', false);
    });
    document.addEventListener('visibilitychange', () => setPaused('hidden', document.hidden));
    // Watch the split, not the whole section: the section is taller than many viewports.
    new IntersectionObserver(([entry]) => setPaused('offscreen', !entry.isIntersecting), { threshold: 0.2 })
        .observe(split);

    run();
}

/**
 * Fill the pricing section from the backend's table. The markup ships with the same numbers, so
 * a failed fetch shows the right prices rather than empty slots — this only keeps the page true
 * if the table changes without a redeploy.
 */
async function initPricingSection() {
    const amount = document.getElementById('planProAmount');
    if (!amount) return;

    const pricing = await getPricing();
    const pro = getTier(pricing, 'professional');
    const free = getTier(pricing, 'starter');

    const anonMeta = document.getElementById('planAnonMeta');
    if (anonMeta) anonMeta.textContent = `${pricing.anonymous.per30Days} free tries, no signup`;

    const freeMeta = document.getElementById('planFreeMeta');
    if (freeMeta && free) freeMeta.textContent = `${free.prompts_per_month} enhancements a month`;

    if (!pro) return;
    amount.textContent = formatPrice(pro.price_cents);

    const proMeta = document.getElementById('planProMeta');
    if (proMeta && pro.price_cents_annual) {
        proMeta.textContent = `or ${formatPrice(pro.price_cents_annual)} a year, saving ${annualSavingPercent(pro)}%`;
    }
}

document.addEventListener('DOMContentLoaded', () => {
    // Must run before the first capture so the entry campaign is on every event.
    captureFirstTouch();

    capture('landing_page_viewed', {
        page_title: document.title,
        url: window.location.href
    });

    initHeroShader();
    initHeroEnhancer();
    initBackToEditor();
    initSmoothScroll();
    initPricingSection();
    initClamps();
    initReceiptsGauge();

    if (typeof lucide !== 'undefined') {
        lucide.createIcons();
    }
});
